"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { cva } from "class-variance-authority";

import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { cn } from "@/lib/utils";

const SilverPlayButton = dynamic(() => import("./icons/silver.svg"));
const GoldPlayButton = dynamic(() => import("./icons/gold.svg"));
const DiamondPlayButton = dynamic(() => import("./icons/diamond.svg"));

const cardVariants = cva(
  [
    "relative flex flex-col-reverse md:flex-row gap-x-4",
    "p-3 md:p-4 rounded-xl min-h-34 overflow-hidden",
    "before:absolute before:-top-3.5 before:right-18 md:before:right-32 before:w-4 before:h-42",
    "before:bg-white/40 before:blur-px before:rotate-25 before:pointer-events-none",
    "after:absolute after:-top-3.5 after:right-13 md:after:right-27 after:w-2.5 after:h-42",
    "after:bg-white/40 after:blur-px after:rotate-25 after:pointer-events-none",
  ],
  {
    variants: {
      variant: {
        silver: "bg-gradient-youtube-silver-badge",
        gold: "bg-gradient-youtube-gold-badge",
        diamond: "bg-gradient-youtube-diamond-badge",
      },
    },
    defaultVariants: {
      variant: "silver",
    },
  }
);

function formatNumber(num) {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return num.toString();
}

function YoutubeChannelCardPlayButton({ variant, ...props }) {
  if (variant === "silver") return <SilverPlayButton {...props} />;
  if (variant === "gold") return <GoldPlayButton {...props} />;
  if (variant === "diamond") return <DiamondPlayButton {...props} />;
}

function YoutubeChannelCard({
  title,
  channelLink,
  subscribersCount,
  className,
  variant = "silver",
}) {
  const trackMoengageEvent = useMoengageEventTracker();

  const handleOnYoutubeChannelClick = (channelName, channelUrl) => () => {
    trackMoengageEvent(EVENT_NAME.YT_CHANNEL_CLICKED, {
      [EVENT_ATTRIBUTE.YT_CHANNEL_NAME]: channelName,
      [EVENT_ATTRIBUTE.YT_CHANNEL_URL]: channelUrl,
    });
  };

  return (
    <Link
      href={channelLink}
      className={cn(cardVariants({ variant, className }))}
      onClick={handleOnYoutubeChannelClick(title, channelLink)}
      target="_blank"
    >
      <div className="flex flex-1 flex-col gap-y-2 pt-2 pl-2 z-10">
        {title && <h6 className="text-base font-medium">{title}</h6>}
        {subscribersCount && (
          <p className="text-sm text-grayscale-text space-x-1">
            <span className="text-sm font-semibold text-header-weak">
              {formatNumber(subscribersCount)}
            </span>
            <span className="text-xs text-grayscale-label">Subscribers</span>
          </p>
        )}
      </div>

      <YoutubeChannelCardPlayButton
        variant={variant}
        viewBox="0 0 80 56"
        className="w-13 md:w-auto md:justify-end md:self-end"
      />
    </Link>
  );
}

export default YoutubeChannelCard;
